"use client";
import * as Headless from "@headlessui/react";
import clsx from "clsx";
import { LayoutGroup, motion } from "framer-motion";
import React, { useId } from "react";
import { TouchTarget } from "./button";
import { Link } from "./link";
import { usePathname } from "next/navigation";

export function Navbar({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"nav">) {
  return <nav {...props} className={clsx(className, "flex items-center")} />;
}

export function NavbarDivider({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={clsx(className, "h-6 w-px bg-zinc-950/10 dark:bg-white/10")}
    />
  );
}

export function NavbarSection({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  const id = useId();

  return (
    <LayoutGroup id={id}>
      <div {...props} className={clsx(className, "flex items-center gap-2")} />
    </LayoutGroup>
  );
}

export function NavbarSpacer({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={clsx(className, "-ml-4 flex-1")}
    />
  );
}

export const NavbarItem = React.forwardRef(function NavbarItem(
  {
    current,
    active,
    className,
    children,
    large = true,
    cart,
    trigger,
    ...props
  }: {
    active?: boolean;
    current?: boolean;
    className?: string;
    large?: boolean;
    cart?: boolean;
    trigger?: boolean;
    children: React.ReactNode;
  } & (
    | Omit<Headless.ButtonProps, "className">
    | Omit<React.ComponentPropsWithoutRef<typeof Link>, "className">
  ),
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>,
) {
  const pathname = usePathname();
  const classes = clsx(
    cart ? "!text-black" : "",
    large ? "text-base text-black" : "text-sm text-gray-500",
    // Base
    "relative flex min-w-0 items-center gap-3 rounded-lg p-2 text-left font-semibold",
    // Leading icon/icon-only
    "data-[slot=icon]:*:size-6 data-[slot=icon]:*:shrink-0 data-[slot=icon]:*:fill-zinc-500 sm:data-[slot=icon]:*:size-5",
    // Trailing icon (down chevron or similar)
    "data-[slot=icon]:last:[&:not(:nth-child(2))]:*:ml-auto data-[slot=icon]:last:[&:not(:nth-child(2))]:*:size-5 sm:data-[slot=icon]:last:[&:not(:nth-child(2))]:*:size-4",
    // Avatar
    "data-[slot=avatar]:*:-m-0.5 data-[slot=avatar]:*:size-7 data-[slot=avatar]:*:[--avatar-radius:theme(borderRadius.DEFAULT)] data-[slot=avatar]:*:[--ring-opacity:10%] sm:data-[slot=avatar]:*:size-6",
    // Hover
    "data-[hover]:bg-gray-500/10 data-[slot=icon]:*:data-[hover]:fill-gray-950 data-[hover]:font-semibold data-[hover]:text-black",
    // Active
    "data-[active]:bg-white data-[active]:hover:bg-white data-[slot=icon]:*:data-[active]:fill-gray-950 data-[active]:font-semibold data-[active]:text-black data-[active]:shadow",
    // Dark mode
    "dark:text-white dark:data-[slot=icon]:*:fill-zinc-400",
    "dark:data-[hover]:bg-white/5 dark:data-[slot=icon]:*:data-[hover]:fill-white",
    "dark:data-[active]:bg-white/5 dark:data-[slot=icon]:*:data-[active]:fill-white",
  );

  return (
    <span className={clsx(className, "relative")}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className={`absolute h-0.5 rounded-full bg-zinc-950 dark:bg-white
            ${
              pathname.includes("/procure")
                ? "-bottom-[1.16rem] inset-x-4"
                : "-bottom-[1.16rem] inset-x-4 "
            }`}
        />
      )}
      {"href" in props ? (
        <Link
          {...props}
          className={classes}
          data-current={current ? "true" : undefined}
          data-active={active ? "true" : undefined}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
          <TouchTarget>{children}</TouchTarget>
        </Link>
      ) : trigger ? (
        <span
          {...props}
          className={clsx("cursor-pointer", classes, "hover:bg-gray-500/10")}
          data-current={current ? "true" : undefined}
          data-active={active ? "true" : undefined}
          ref={ref}>
          <TouchTarget>{children}</TouchTarget>
        </span>
      ) : (
        <Headless.Button
          {...props}
          className={clsx("cursor-pointer", classes)}
          data-current={current ? "true" : undefined}
          data-active={active ? "true" : undefined}
          ref={ref}>
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  );
});

export function NavbarLabel({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"span">) {
  return <span {...props} className={clsx(className, "truncate")} />;
}
